import { forwardRef, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';

import clsx from 'clsx';
import { motion } from 'framer-motion';

// Hooks
import useAcl from '@/hooks/user/useAcl';
import {
    UPDATE_CALENDAR_FILTER_POST_STATUS,
    UPDATE_CALENDAR_MEDIA_LIBRARY,
    useCalendar,
    useCalendarViewChange
} from '@/utils/hooks/scheduler/calendar';
import useCollaborationSettingsLink from '@/utils/hooks/swr/useCollaborationSettingsLink';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import {
    calendarBttpLocalStorageKey,
    calendarFoLocalStorageKey,
    calendarSmcLocalStorageKey,
    calendarTimeSlotsLocalStorageKey
} from '@/utils/localStorage/scheduler/calendarFiltersLocalStorage';
import useToggle from '@/utils/hooks/utils/useToggle';

// Lib
import { aclFeatures } from '@/lib/acl/aclFeatures';
import { POST_STATUS } from '@/lib/scheduler/postStatus';
import { FACEBOOK, INSTAGRAM } from '@/lib/socialProfile';

// Components
import Icon from '@/components/base/Icon';
import Tooltip from '@/components/base/Tooltip';
import SelectCalendarView from '@/components/composite/Scheduler/Calendar/SelectCalendarView';
import SelectFilterStatusPost from '@/components/composite/Scheduler/Calendar/SelectFilterStatusPost';
import { NavigationButtons } from '@/components/pages/scheduler/calendar/CalendarHeader/NavigationButtons';
import { ButtonCopyLink } from '@/components/pages/scheduler/SchedulerHeader/ButtonCopyLink';

const FilterCalendarEvents = dynamic(
    () => import('@/components/pages/scheduler/calendar/FilterCalendarEvents')
);
const CreateSocialMediaEventModal = dynamic(
    () =>
        import(
            '@/components/pages/scheduler/calendar/social-media-events/CreateSocialMediaEventModal'
        )
);
const TimeSlotsListPopover = dynamic(
    () => import('@/components/pages/scheduler/calendar/timeslots/TimeSlotsListPopover')
);

const statusOptions = [
    {
        label: 'scheduler:status.all',
        value: 'all'
    },
    ...Object.keys(POST_STATUS).map((statusKey) => {
        const status = POST_STATUS[statusKey];
        return {
            label: status.text,
            value: statusKey,
            color: status.color,
            permissions: status.permissions
        };
    })
];

const eventsList = [
    {
        id: 'best_time_to_post',
        icon: <Icon icon="star" className="text-icoYellow" />,
        cookie: calendarBttpLocalStorageKey,
        label: 'scheduler:calendar.events.show_best_time_to_post'
    },
    {
        id: 'smc',
        icon: <Icon icon="calendar" className="text-icoBlue" />,
        cookie: calendarSmcLocalStorageKey,
        label: 'scheduler:calendar.events.show_social_media_calendar'
    },
    {
        id: 'followers_online',
        icon: <Icon icon="followers" className="text-icoBlue" />,
        cookie: calendarFoLocalStorageKey,
        label: {
            [INSTAGRAM]: 'scheduler:calendar.events.show_followers_online',
            [FACEBOOK]: 'scheduler:calendar.events.show_fans_online'
        }
    },
    {
        id: 'time_slots',
        icon: <Icon icon="likes-on" className="text-icoRed" />,
        cookie: calendarTimeSlotsLocalStorageKey,
        label: 'scheduler:calendar.events.show_time_slots'
    }
];

const CalendarHeader = forwardRef(
    ({ mediaLibraryGuidanceAllowed, isGroup, activeGroup, activeSocialProfile }, ref) => {
        const { calendarData, dispatchCalendar } = useCalendar();
        const { isOpen: isOpenCreateModal, toggle: toggleCreateModal } = useToggle();
        const { t } = useTranslation('scheduler');
        const { hasAcl } = useAcl();

        const [showMediaLibraryGuide, setShowMediaLibraryGuide] = useState(false);

        const { link } = useCollaborationSettingsLink({
            ...(isGroup ? { activeGroupId: activeGroup?.id } : { SPID: activeSocialProfile?.id }),
            options: { revalidateOnFocus: false, revalidateOnReconnect: false }
        });
        const handleViewChange = useCalendarViewChange(ref);

        useEffect(() => {
            if (mediaLibraryGuidanceAllowed && calendarData.openMediaLibrary.active === null) {
                setTimeout(() => {
                    setShowMediaLibraryGuide(true);
                }, 1500);
            }
        }, [mediaLibraryGuidanceAllowed]);

        return (
            <div className="manipulation-header z-10 box-content flex h-14 flex-wrap rounded-t-xl border-b border-icoGray-300 bg-white dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite">
                <div className="flex flex-1 items-center justify-between pl-3 pr-5">
                    <NavigationButtons ref={ref} />
                    <div className="flex h-full flex-1 items-center justify-end gap-4">
                        <div className="flex items-center gap-2">
                            <SelectCalendarView
                                className="hidden lg:block"
                                viewSelected={calendarData.view}
                                buttonVariant="whiteWithBorder"
                                onChange={handleViewChange}
                            />
                            <SelectFilterStatusPost
                                statusSelected={calendarData.filter_post_status}
                                statusOptions={statusOptions.filter((status) => {
                                    if (status.permissions) {
                                        if (hasAcl(status.permissions)) {
                                            return status;
                                        }

                                        return;
                                    }

                                    return status;
                                })}
                                onChange={({ value }) => {
                                    dispatchCalendar({
                                        type: UPDATE_CALENDAR_FILTER_POST_STATUS,
                                        payload: value
                                    });
                                }}
                            />
                        </div>
                        <div className="flex h-full items-center gap-4">
                            <hr className="h-4 w-px bg-icoGray-300 dark:border-icoDarkMode-beton" />
                            <button
                                onClick={toggleCreateModal}
                                className="flex items-center"
                                title={t('scheduler:custom_events.modal.create_event')}
                                data-testid="button-create-event">
                                <Icon
                                    icon="calendar-add"
                                    className="text-icoGray-500 hover:text-icoBlue dark:text-icoDarkMode-wolf dark:hover:text-icoBlue"
                                />
                            </button>
                            <CreateSocialMediaEventModal
                                isOpen={isOpenCreateModal}
                                toggle={toggleCreateModal}
                            />
                            <TimeSlotsListPopover
                                isGroup={isGroup}
                                activeGroup={activeGroup}
                                activeSocialProfile={activeSocialProfile}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex h-full items-center">
                    {link?.hash && hasAcl(aclFeatures.publishing.external_collaboration.read) ? (
                        <ButtonCopyLink
                            displayIcon
                            link={`${process.env.NEXT_PUBLIC_BASE_URL}collaborate-area/${link?.hash}`}
                            className={clsx([
                                'flex h-full items-center rounded-none px-5',
                                'text-icoGray-500 hover:text-icoBlue dark:text-icoDarkMode-wolf dark:hover:text-icoBlue',
                                'border-l border-icoGray-300 dark:border-icoDarkMode-beton',
                                'focus:ring-0 focus:ring-offset-0'
                            ])}
                        />
                    ) : null}
                    <FilterCalendarEvents
                        eventsList={eventsList}
                        isTreeStructureActivated
                        network={activeSocialProfile?.social_network_type}
                    />
                    {!isGroup ? (
                        <Tooltip content={t('media_library.your_media_library')}>
                            <button
                                onClick={() => {
                                    dispatchCalendar({
                                        type: UPDATE_CALENDAR_MEDIA_LIBRARY,
                                        payload: !calendarData.openMediaLibrary.active
                                    });
                                }}
                                className="flex h-full items-center rounded-none border-l border-icoGray-300 px-5 focus:ring-0 focus:ring-offset-0 dark:border-icoDarkMode-beton">
                                <span className="relative">
                                    <Icon
                                        icon="media-library"
                                        className={clsx(
                                            'hover:text-icoBlue dark:hover:text-icoBlue',
                                            {
                                                'text-icoGray-500 dark:text-icoDarkMode-wolf':
                                                    !calendarData.openMediaLibrary.active,
                                                'text-icoBlue': calendarData.openMediaLibrary.active
                                            }
                                        )}
                                    />
                                    {calendarData.openMediaLibrary.active === null &&
                                    showMediaLibraryGuide ? (
                                        <span className="absolute left-1/2 top-1/2 block h-5 w-5 -translate-x-1/2 -translate-y-1/2">
                                            <motion.span
                                                className="absolute block h-5 w-5 origin-center rounded-full border border-icoBlue"
                                                initial={{ opacity: 1, scale: 1 }}
                                                animate={{ opacity: 0, scale: 3.5 }}
                                                transition={{
                                                    repeat: 1,
                                                    duration: 0.6
                                                }}
                                                onAnimationComplete={() => {
                                                    dispatchCalendar({
                                                        type: UPDATE_CALENDAR_MEDIA_LIBRARY,
                                                        payload:
                                                            !calendarData.openMediaLibrary.active
                                                    });
                                                }}></motion.span>
                                        </span>
                                    ) : null}
                                </span>
                            </button>
                        </Tooltip>
                    ) : null}
                </div>
            </div>
        );
    }
);

CalendarHeader.displayName = 'CalendarHeader';

export default CalendarHeader;
